// Docker-Aggregat und Container-Liste: EIN gemeinsamer Stand für die Cluster-
// Infra-Karte (running/total), die Docker-Seite, die Server-Seite und den
// Inventar-Index der TopBar-Suche.
//
// Datenfluss (seit 03.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Docker über das Protokoll“): jeder Container ist eine Instanz
// docker.<id>.stand (Zustand, Image, Ports, CPU, Speicher, GPU), die Rechner in
// ihrer Reihenfolge und ob sie antworten stehen in docker.lage. Das Backend
// (internal/protokoll/docker) sammelt per SSH auf allen Rechnern -- teuer, ~2 s
// je Rechner --, deshalb NUR, solange jemand docker.lage liest: alle 30 s und
// auf Anstoß (eine Aktion, ein neuer Stand im Cache der Liste). Der Hub
// verwirft Unverändertes (Totbänder für CPU und Speicher) und schickt Deltas.
// Bis dahin holte jeder Tab /v2-api/docker/containers alle 30 s und bei jedem
// WS-Signal "docker" die ganze Liste.
//   - Hier stehen die einzigen Abo-Stellen (`docker.*.stand`, `docker.lage`). Aus
//     den Ständen baut lib/docker-protokoll.ts die Rechner mit Containern in
//     genau der Form der REST-Antwort.
//   - REST (`GET /v2-api/docker/containers`) nur beim Start, als Revalidator
//     (Reconnect, Tab kommt zurück), in refreshDockerSummary (nach einer
//     Aktion) und als Neuversuch alle 15 s, solange ein Abruf scheiterte. KEIN
//     Takt, KEIN Abo auf den alten WS-Kanal "docker" mehr. Der Kanal bleibt:
//     er ist das Änderungssignal der Stacks-, Netzwerk- und Aufräum-Panels
//     (abonniert nur, solange eins offen ist).
//   - Stände und Abrufe mischen wie in agents-summary (ProtokollListe): Stände
//     in-place, ein Stand während eines Abrufs liegt über dessen Antwort, ein
//     `weg` hinterlässt einen Grabstein. Bis `docker.lage` da ist, gibt die
//     Antwort die Rechner vor (Reihenfolge, Name, erreichbar).
//   - Die Rechner-Liste (`getDockerHosts`) behält ihre Objekte, solange sich an
//     einem Rechner nichts ändert; die Ablage wird gedrosselt (höchstens alle
//     2 s) geschrieben, nicht bei jedem Stand.
//
// SWR: ja -- die zuletzt gesehene Liste kommt beim Start aus der Ablage
// (Schlüssel "docker-hosts-2"; seit 03.11.2026 sind die Werte Zahlen, die
// Ablage unter "docker-hosts" mit Texten liest niemand mehr). Die Zahlen der
// Infra-Karte rechnet der Store aus der Liste.

import { getJSON } from "./api";
import {
  dockerEintragAus,
  dockerEintraegeAusHosts,
  dockerHostsAus,
  dockerHostsStabil,
  dockerIdAusInstanz,
  dockerLageAusHosts,
  dockerSchluessel,
  vergleicheDockerEintrag,
  type DockerEintrag,
} from "./docker-protokoll";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { ProtokollListe } from "./protokoll-liste.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { DockerLage, DockerRechner, DockerStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";
import { gleich } from "./storage-protokoll";
import type { DockerHost } from "./types";

export interface DockerSummary {
  running: number;
  total: number;
}

const STORAGE_KEY_HOSTS = "docker-hosts-2";

const _liste = new ProtokollListe<DockerEintrag>((e) => dockerSchluessel(e.rechner, e.container.name), vergleicheDockerEintrag);
// Die Rechner aus der letzten Antwort (oder der Ablage): gelten, bis docker.lage
// da ist. `docker.lage` selbst: `undefined` = noch nicht gekommen.
let _restLage = $state.raw<DockerRechner[]>([]);
let _lage = $state.raw<DockerLage | undefined>(undefined);
// Ob der Stand etwas weiß (Ablage, Abruf oder Protokoll): vorher kein Aggregat.
let _geladen = $state(false);
let _started = false;
let _unsub: Array<() => void> = [];
// Instanzname -> Schlüssel des Containers: `weg` nennt nur die Instanz.
const _instanzen = new Map<string, string>();

// ── Persistenz ───────────────────────────────────────────────────────

// Aus dem Protokoll kommen alle 30 s Stände für viele Container: gespeichert
// wird gebündelt, höchstens alle 2 s -- und nur, wenn sich die Liste geändert hat.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    persist(STORAGE_KEY_HOSTS, $state.snapshot(_hosts));
  }, 2_000);
}

// ── Stände ──────────────────────────────────────────────────────────

function beiStand(instanz: string, daten: Record<string, unknown>): void {
  if (dockerIdAusInstanz(instanz) === undefined) return;
  const e = dockerEintragAus(skaliere<DockerStand>("docker.stand", daten));
  if (!e) return;
  const k = dockerSchluessel(e.rechner, e.container.name);
  _instanzen.set(instanz, k);
  const geaendert = _liste.stand(e);
  // Vor dem ersten Abruf legt laden() die Container über die Antwort; bis dahin
  // wäre die Liste leer, obwohl Stände da sind.
  if (_liste.bereit) _geladen = true;
  if (geaendert) persistSpaeter();
}

function beiWeg(instanz: string): void {
  const k = _instanzen.get(instanz);
  if (k === undefined) return;
  _instanzen.delete(instanz);
  if (_liste.weg(k)) persistSpaeter();
}

function beiLage(_instanz: string, daten: Record<string, unknown>): void {
  const l = skaliere<DockerLage>("docker.lage", daten);
  if (_lage && gleich(_lage, l)) return;
  _lage = l;
  if (_liste.bereit) _geladen = true;
  persistSpaeter();
}

// ── REST ─────────────────────────────────────────────────────────────

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst erst
// der nächste Revalidator -- und bis dahin bliebe ein aus der Ablage geholter,
// inzwischen verschwundener Container stehen (für den kommt kein `weg`, diese
// Sitzung kannte seine Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function laden(): Promise<void> {
  const ab = _liste.nr;
  const lauf = _lauf;
  let rest: DockerHost[] | undefined;
  try {
    rest = await getJSON<DockerHost[]>("/v2-api/docker/containers");
  } catch {
    rest = undefined;
  }
  if (lauf !== _lauf) return;
  try {
    // Was das Protokoll während eines gescheiterten Abrufs gebracht hat, gilt
    // trotzdem -- beim ersten Abruf wäre die Liste sonst leer, bis jeder
    // Container sich ändert.
    if (rest) {
      const lage = dockerLageAusHosts(rest);
      if (!gleich(_restLage, lage)) _restLage = lage;
    }
    if (_liste.mische(rest ? dockerEintraegeAusHosts(rest) : undefined, ab)) persistSpaeter();
    if (rest || _liste.neuer(ab)) _geladen = true;
  } finally {
    _liste.bereit = true;
  }
  if (!rest && _started && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_started) void laden();
    }, NEUVERSUCH_MS);
  }
}

export function startDockerSummary(): void {
  if (_started) return;
  _started = true;
  _lauf++;
  // SWR: die gecachte Liste sofort einspielen. Das Protokoll bringt die
  // frischen Werte danach in Sekunden, der Cache deckt nur die First-Paint-Lücke.
  const cachedHosts = loadPersisted<DockerHost[]>(STORAGE_KEY_HOSTS);
  if (cachedHosts) {
    _restLage = dockerLageAusHosts(cachedHosts);
    _liste.items = dockerEintraegeAusHosts(cachedHosts);
    _geladen = true;
  }
  void laden();
  _unsub.push(
    abonniere("docker.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg }),
    abonniere("docker.lage", { rate: "normal" }, { stand: beiLage }),
  );
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
  // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
  // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch.
  _unsub.push(registerRevalidator(laden));
}

export function stopDockerSummary(): void {
  _started = false;
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _liste.leere();
  _instanzen.clear();
  _restLage = [];
  _lage = undefined;
  _geladen = false;
  _vorher = undefined;
}

// ── Getter ───────────────────────────────────────────────────────────

const KEINE_HOSTS: DockerHost[] = [];
let _vorher: DockerHost[] | undefined;

// Die Rechner mit ihren Containern. Neu gerechnet nur bei geänderter Eingabe; die
// Rechner und Container, die sich nicht ändern, behalten ihr Objekt.
const _hosts = $derived.by((): DockerHost[] => {
  if (!_geladen) return KEINE_HOSTS;
  const next = dockerHostsStabil(dockerHostsAus(_lage?.rechner ?? _restLage, _liste.items), _vorher);
  _vorher = next;
  return next;
});

function zaehle(hosts: readonly DockerHost[]): DockerSummary {
  let running = 0;
  let total = 0;
  for (const h of hosts) {
    total += h.containers.length;
    running += h.containers.filter((c) => c.state === "running").length;
  }
  return { running, total };
}

let _summaryVorher: DockerSummary | undefined;
const _summary = $derived.by((): DockerSummary | undefined => {
  if (!_geladen) return undefined;
  const next = zaehle(_hosts);
  if (_summaryVorher && _summaryVorher.running === next.running && _summaryVorher.total === next.total) return _summaryVorher;
  _summaryVorher = next;
  return next;
});

/** Die Zahlen für die Infra-Karte; `undefined`, bis Ablage, Abruf oder Protokoll etwas geliefert haben. */
export function getDockerSummary(): DockerSummary | undefined {
  return _summary;
}

// Voller Host/Container-Snapshot. Wird vom Inventar-Index der
// TopBar-Suche + Docker-Page benutzt. SWR via localStorage; leer nur
// beim allerersten Reload.
export function getDockerHosts(): DockerHost[] {
  return _hosts;
}

// Manueller Trigger nach Container-Aktion (start/stop/restart) -- nicht auf das
// Protokoll warten (der Dienst sammelt neu, das dauert ein paar Sekunden).
export function refreshDockerSummary(): Promise<void> {
  // Ohne Start (Seite verlassen, Aktion endet später) nichts mehr füllen.
  if (!_started) return Promise.resolve();
  return laden();
}
